import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, FileDown, Sheet } from "lucide-react";
import { api, download, errCode } from "../lib/api";
import { useLang } from "../lib/i18n";
import { todayCL, fmtTime } from "../lib/time";
import { PageTitle } from "../components/Common";
import { ScopeFilters } from "./Bins";

const monday = (iso) => { const d = new Date(`${iso}T12:00:00Z`); d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7)); return d.toISOString().slice(0, 10); };
const shift = (iso, days) => { const d = new Date(`${iso}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + days); return d.toISOString().slice(0, 10); };
const isoWeek = (iso) => { const d = new Date(`${iso}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + 3 - ((d.getUTCDay() + 6) % 7)); const y = new Date(Date.UTC(d.getUTCFullYear(), 0, 4)); return 1 + Math.round(((d - y) / 864e5 - 3 + ((y.getUTCDay() + 6) % 7)) / 7); };
const fmt = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("es-CL", { maximumFractionDigits: 2 }));

const COLS = ["bins", "verified_bins", "verified_kg", "avg_net_kg", "pickers", "picker_hours", "kg_per_picker", "kg_per_picker_hour"];
const PCOLS = ["pickers", "picker_hours", "verified_kg", "kg_per_picker_hour", "kg_per_picker"];

const ReportTable = ({ title, rows, cols, total, testid, t }) => (
  <div className="card overflow-x-auto" data-testid={testid}>
    <h3 className="font-bold px-4 py-3 border-b">{title}</h3>
    <table className="tbl w-full">
      <thead><tr><th>{t("name")}</th>{cols.map((c) => <th key={c}>{t(`rc_${c}`)}</th>)}</tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id || "x"} data-testid={`${testid}-row`}>
            <td className="font-semibold min-w-[140px]">{r.name}{r.missing_hours && <span className="block text-xs font-bold text-amber-800">{t("missing_hours")}</span>}</td>
            {cols.map((c) => <td key={c} className="font-mono">{fmt(r[c])}</td>)}
          </tr>))}
        {total && <tr className="bg-slate-100 font-bold" data-testid={`${testid}-total`}><td>TOTAL</td>{cols.map((c) => <td key={c} className="font-mono">{fmt(total[c])}</td>)}</tr>}
        {!rows.length && <tr><td colSpan={cols.length + 1} className="text-slate-500">{t("none")}</td></tr>}
      </tbody>
    </table>
  </div>
);

const DriversView = ({ d, t, lang }) => (
  <>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {[["drivers", d.total.drivers], ["trips", d.total.trips], ["bins", d.total.bins], ["net_kg", d.total.net_kg]].map(([k, v]) => (
        <div key={k} className="card p-4" data-testid={`driver-kpi-${k}`}><p className="eyebrow">{t(`dr_${k}`)}</p><p className="font-display text-3xl font-extrabold mt-1">{fmt(v)}</p></div>))}
    </div>
    <div className="card overflow-x-auto" data-testid="driver-by-day">
      <h3 className="font-bold px-4 py-3 border-b">{t("by_day")}</h3>
      <table className="tbl w-full">
        <thead><tr><th>{t("date")}</th><th>{t("dp_driver")}</th><th>{t("dp_plate")}</th><th>{t("dr_trips")}</th><th>{t("dr_bins")}</th><th>{t("dr_net_kg")}</th><th>{t("dr_window")}</th></tr></thead>
        <tbody>
          {d.by_day.map((r) => (
            <tr key={`${r.date}-${r.driver_name}-${r.truck_plate}`} data-testid="driver-by-day-row">
              <td className="font-mono text-xs">{r.date}</td><td className="font-semibold">{r.driver_name}</td><td className="font-mono">{r.truck_plate}</td>
              <td className="font-mono">{r.trips}</td><td className="font-mono">{r.bins}</td><td className="font-mono">{fmt(r.net_kg)}</td>
              <td className="font-mono text-xs">{fmtTime(r.first_at, lang)} – {fmtTime(r.last_at, lang)}</td></tr>))}
          {!d.by_day.length && <tr><td colSpan={7} className="text-slate-500">{t("none")}</td></tr>}
        </tbody>
      </table>
    </div>
    <div className="card overflow-x-auto" data-testid="driver-by-driver">
      <h3 className="font-bold px-4 py-3 border-b">{t("dr_week_total")}</h3>
      <table className="tbl w-full">
        <thead><tr><th>{t("dp_driver")}</th><th>{t("dp_plate")}</th><th>{t("dr_trips")}</th><th>{t("dr_bins")}</th><th>{t("dr_net_kg")}</th></tr></thead>
        <tbody>
          {d.by_driver.map((r) => (
            <tr key={`${r.driver_name}-${r.truck_plate}`} data-testid="driver-by-driver-row"><td className="font-semibold">{r.driver_name}</td><td className="font-mono">{r.truck_plate}</td>
              <td className="font-mono">{r.trips}</td><td className="font-mono">{r.bins}</td><td className="font-mono">{fmt(r.net_kg)}</td></tr>))}
          {!!d.by_driver.length && <tr className="bg-slate-100 font-bold" data-testid="driver-total-row"><td colSpan={2}>TOTAL</td><td className="font-mono">{d.total.trips}</td><td className="font-mono">{d.total.bins}</td><td className="font-mono">{fmt(d.total.net_kg)}</td></tr>}
          {!d.by_driver.length && <tr><td colSpan={5} className="text-slate-500">{t("none")}</td></tr>}
        </tbody>
      </table>
    </div>
  </>
);

export default function Reports() {
  const { t, te, lang } = useLang();
  const [period, setPeriod] = useState("week");
  const [start, setStart] = useState(monday(todayCL()));
  const [q, setQ] = useState({ grower_id: "", farm_id: "" });
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const drv = period === "drivers";
  const weekly = period !== "season";
  const params = Object.fromEntries(Object.entries({ period: drv ? "" : period, start: weekly ? start : "", ...q }).filter(([, v]) => v));
  useEffect(() => {
    setD(null);
    api.get(drv ? "/reports/drivers" : "/reports/summary", { params }).then((r) => setD(r.data)).catch((e) => toast.error(te(errCode(e))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, start, q.grower_id, q.farm_id]);
  const dl = async (ext) => {
    setBusy(ext);
    try { await download(drv ? "/reports/drivers.csv" : `/reports/summary.${ext}`, `365fresh-${period}-${d?.from || start}.${ext}`, { params }); } catch (e) { toast.error(te(errCode(e))); }
    setBusy("");
  };
  const tab = (p) => `px-4 py-2 rounded-lg text-sm font-bold border-2 transition-colors ${period === p ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 hover:border-slate-500"}`;

  return (
    <div className="space-y-5" data-testid="reports-page">
      <PageTitle right={<div className="flex gap-2">
        {!drv && <button className="btn-secondary btn-sm" disabled={!d || busy} onClick={() => dl("pdf")} data-testid="report-download-pdf"><FileDown size={16} />{busy === "pdf" ? "…" : "PDF"}</button>}
        <button className="btn-secondary btn-sm" disabled={!d || busy} onClick={() => dl("csv")} data-testid="report-download-csv"><Sheet size={16} />{busy === "csv" ? "…" : t("spreadsheet")}</button>
      </div>}>{t("reports_title")}</PageTitle>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-2">
          <button className={tab("week")} onClick={() => setPeriod("week")} data-testid="report-tab-week">{t("report_week")}</button>
          <button className={tab("season")} onClick={() => setPeriod("season")} data-testid="report-tab-season">{t("report_season")}</button>
          <button className={tab("drivers")} onClick={() => setPeriod("drivers")} data-testid="report-tab-drivers">{t("report_drivers")}</button>
        </div>
        {weekly && <div className="flex items-center gap-2" data-testid="week-picker">
          <button className="btn-secondary btn-sm" onClick={() => setStart(shift(start, -7))} data-testid="week-prev"><ChevronLeft size={16} /></button>
          <span className="font-mono font-bold text-xs sm:text-sm whitespace-nowrap text-center" data-testid="week-label">{t("week_short")} {isoWeek(start)} · {start} → {shift(start, 6)}</span>
          <button className="btn-secondary btn-sm" onClick={() => setStart(shift(start, 7))} disabled={start >= monday(todayCL())} data-testid="week-next"><ChevronRight size={16} /></button>
        </div>}
        {period === "season" && d?.season && <span className="font-mono font-bold text-sm" data-testid="season-label">{d.season.name} · {d.from} → {d.to}</span>}
      </div>
      <ScopeFilters q={q} setQ={setQ} noDate />
      {!d || (drv ? !d.by_day : !d.farms) ? <p>{t("loading")}</p> : drv ? <DriversView d={d} t={t} lang={lang} /> : <>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[["bins", d.total?.bins ?? 0], ["verified_kg", d.total?.verified_kg ?? 0], ["avg_net_kg", d.total?.avg_net_kg], ["kg_per_picker_hour", d.total?.kg_per_picker_hour]].map(([k, v]) => (
            <div key={k} className="card p-4" data-testid={`report-kpi-${k}`}><p className="eyebrow">{t(`rc_${k}`)}</p><p className="font-display text-3xl font-extrabold mt-1">{fmt(v)}</p></div>))}
        </div>
        <ReportTable title={t("by_farm")} rows={d.farms} cols={COLS} total={d.farms.length > 1 ? d.total : null} testid="report-by-farm" t={t} />
        <ReportTable title={period === "week" ? t("by_day") : t("by_week")} rows={d.periods} cols={COLS} testid="report-by-period" t={t} />
        <div className="grid gap-4">
          <ReportTable title={t("by_contractor")} rows={d.by_contractor} cols={PCOLS} testid="report-by-contractor" t={t} />
          <ReportTable title={t("by_crew")} rows={d.by_crew} cols={PCOLS} testid="report-by-crew" t={t} />
        </div>
      </>}
    </div>
  );
}
